import React from "react";
import { useTranslation } from "react-i18next";
import { ClosedPeriodBridgeOffer } from "../../features/absences/use-closed-period-bridge";

interface ClosedPeriodBridgeDialogProps {
  offer: ClosedPeriodBridgeOffer;
  busy: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

/** Offers to skip subscription billing for a recorded absence (per-member closed period). */
const ClosedPeriodBridgeDialog: React.FC<ClosedPeriodBridgeDialogProps> = ({
  offer,
  busy,
  onConfirm,
  onDismiss,
}) => {
  const { t, i18n } = useTranslation();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const range =
    offer.startDate === offer.endDate
      ? dateFormat.format(new Date(`${offer.startDate}T00:00:00`))
      : `${dateFormat.format(new Date(`${offer.startDate}T00:00:00`))} – ${dateFormat.format(
          new Date(`${offer.endDate}T00:00:00`),
        )}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl lg:p-6 dark:bg-primary-10">
        <h3 className="text-grey-10 dark:text-grey-90">
          {t("absences.bridge.title", "Also skip billing for this time?")}
        </h3>
        <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
          {t(
            "absences.bridge.body",
            "{{name}} has an active subscription. Create a closed period ({{range}}, not charged) so the monthly draft skips this absence?",
            { name: offer.memberName, range },
          )}
        </p>

        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onDismiss} disabled={busy}>
            {t("absences.bridge.skip", "No, billing stays as is")}
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy}>
            {t("absences.bridge.confirm", "Create closed period")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClosedPeriodBridgeDialog;
